// Relationship Graph Data
// Maps all frameworks, their metadata, and relationships

import type {
  RelationshipGraph,
  FrameworkMetadata,
  FrameworkRelationship,
  SpatialLocation,
} from '../types/relationships';

// ===== FRAMEWORK METADATA =====

export const FRAMEWORK_METADATA: FrameworkMetadata[] = [
  // DEGAP Framework
  {
    encodedId: 'pf.degap.stages',
    name: 'Five Stages of DEGAP',
    category: 'framework',
    spatialLocations: [
      {
        type: 'island-region',
        id: 'stakeholder-intel',
        label: 'Western Shore',
        description: 'Team-scope capability region',
        icon: '🏝️',
      },
      {
        type: 'mountain-elevation',
        id: 'all',
        label: 'All Elevations (1000m-7000m)',
        description: 'Progressive journey through all 5 stages',
        icon: '🏔️',
      },
      {
        type: 'mof-layer',
        id: 'm2',
        label: 'M2: Models',
        description: 'Core framework model',
        icon: '🏗️',
      },
    ],
    glossaryTerms: ['DEGAP', 'Decide', 'Engage', 'Gather', 'Arrange', 'Prioritize'],
    tags: {
      scope: 'team',
      domain: ['transformation', 'change-management', 'prioritization'],
    },
    difficulty: 'intermediate',
    prerequisites: [],
    bestFor: [
      'Leading organizational transformations',
      'Structuring strategic initiatives',
      'Facilitating stakeholder engagement',
    ],
    avoidWhen: [
      'Decisions need to be made immediately',
      'Context is extremely simple/obvious',
    ],
  },

  // Relevance Framework
  {
    encodedId: 'gf.relevance.framework',
    name: 'The Relevance Framework',
    category: 'framework',
    spatialLocations: [
      {
        type: 'island-region',
        id: 'conceptual-precision',
        label: 'Eastern Highlands',
        description: 'Individual-scope conceptual clarity',
        icon: '🏝️',
      },
      {
        type: 'mountain-elevation',
        id: 'engage',
        label: '2500m - ENGAGE',
        description: 'Communication and alignment stage',
        icon: '🏔️',
      },
      {
        type: 'mof-layer',
        id: 'm2',
        label: 'M2: Models',
        description: 'Communication model',
        icon: '🏗️',
      },
    ],
    glossaryTerms: ['Relevance Framework', 'Coherence', 'Shallow Communication', 'Ungrounded Inspiration', 'Mechanical Instruction'],
    tags: {
      scope: 'individual',
      domain: ['communication', 'clarity', 'alignment'],
    },
    difficulty: 'beginner',
    prerequisites: [],
    bestFor: [
      'Improving communication clarity',
      'Avoiding common anti-patterns',
      'Ensuring coherent messaging',
    ],
    avoidWhen: [
      'Audience already has deep shared context',
    ],
  },

  // Role-Based Participation
  {
    encodedId: 'pf.framework.diagnostic.role.participation',
    name: 'Role-Based Participation Across Scopes',
    category: 'framework',
    spatialLocations: [
      {
        type: 'island-region',
        id: 'stakeholder-intel',
        label: 'Western Shore',
        description: 'Stakeholder Intelligence region',
        icon: '🏝️',
      },
      {
        type: 'mountain-face',
        id: 'team',
        label: 'Team Face',
        description: 'Team-level scaling',
        icon: '🧗',
      },
      {
        type: 'mof-layer',
        id: 'm2',
        label: 'M2: Models',
        description: 'Role framework',
        icon: '🏗️',
      },
    ],
    glossaryTerms: [],
    tags: {
      scope: 'team',
      domain: ['roles', 'collaboration', 'scaling'],
    },
    difficulty: 'intermediate',
    prerequisites: [],
    bestFor: [
      'Defining team roles',
      'Scaling participation',
      'Clarifying responsibilities',
    ],
  },

  // Role Types
  {
    encodedId: 'pf.framework.diagnostic.role.types',
    name: 'Role Types',
    category: 'framework',
    spatialLocations: [
      {
        type: 'island-region',
        id: 'stakeholder-intel',
        label: 'Western Shore',
        description: 'Understanding stakeholder roles',
        icon: '🏝️',
      },
      {
        type: 'mountain-elevation',
        id: 'engage',
        label: '2500m - ENGAGE',
        description: 'Role identification in engagement',
        icon: '🏔️',
      },
      {
        type: 'mof-layer',
        id: 'm2',
        label: 'M2: Models',
        description: 'Role taxonomy',
        icon: '🏗️',
      },
    ],
    glossaryTerms: [],
    tags: {
      scope: 'team',
      domain: ['roles', 'stakeholders', 'engagement'],
    },
    difficulty: 'beginner',
    prerequisites: [],
    bestFor: [
      'Identifying stakeholder types',
      'Planning engagement strategies',
    ],
  },

  // Multi-Resolution Planning
  {
    encodedId: 'cont.multires.levels',
    name: 'Multi-Resolution Planning Levels',
    category: 'framework',
    spatialLocations: [
      {
        type: 'island-region',
        id: 'tradeoff-intel',
        label: 'Mountain Range',
        description: 'Organization-scope tradeoff decisions',
        icon: '🏝️',
      },
      {
        type: 'mountain-elevation',
        id: 'arrange',
        label: '5500m - ARRANGE',
        description: 'Organizing planning horizons',
        icon: '🏔️',
      },
      {
        type: 'mof-layer',
        id: 'm2',
        label: 'M2: Models',
        description: 'Planning framework',
        icon: '🏗️',
      },
    ],
    glossaryTerms: [],
    tags: {
      scope: 'organization',
      context: 'turbulent',
      domain: ['planning', 'uncertainty', 'adaptation'],
    },
    difficulty: 'advanced',
    prerequisites: ['pf.degap.stages'],
    bestFor: [
      'Planning in uncertain environments',
      'Managing multiple time horizons',
      'Adaptive roadmapping',
    ],
    avoidWhen: [
      'Environment is highly stable',
      'Simple linear planning suffices',
    ],
  },

  // Diagram Levels (Placeholder - to demonstrate relationships)
  {
    encodedId: 'gf.diagram.levels',
    name: 'Diagram Complexity Levels',
    category: 'framework',
    spatialLocations: [
      {
        type: 'island-region',
        id: 'conceptual-precision',
        label: 'Eastern Highlands',
        description: 'Visual communication clarity',
        icon: '🏝️',
      },
      {
        type: 'mof-layer',
        id: 'm2',
        label: 'M2: Models',
        description: 'Visual design framework',
        icon: '🏗️',
      },
    ],
    glossaryTerms: [],
    tags: {
      scope: 'individual',
      domain: ['visualization', 'communication'],
    },
    difficulty: 'beginner',
    prerequisites: [],
    bestFor: [
      'Creating clear diagrams',
      'Visual storytelling',
    ],
  },
];

// ===== FRAMEWORK RELATIONSHIPS =====

export const FRAMEWORK_RELATIONSHIPS: FrameworkRelationship[] = [
  // DEGAP ← → Relevance Framework
  {
    fromId: 'gf.relevance.framework',
    toId: 'pf.degap.stages',
    type: 'complement',
    strength: 0.8,
    description: 'Relevance Framework ensures clear communication throughout DEGAP stages',
    bidirectional: true,
  },

  // DEGAP → Multi-Resolution Planning
  {
    fromId: 'pf.degap.stages',
    toId: 'cont.multires.levels',
    type: 'prerequisite',
    strength: 0.9,
    description: 'Understanding DEGAP stages is essential before applying multi-resolution planning',
    bidirectional: false,
  },

  // Role Types → Role-Based Participation
  {
    fromId: 'pf.framework.diagnostic.role.types',
    toId: 'pf.framework.diagnostic.role.participation',
    type: 'prerequisite',
    strength: 0.9,
    description: 'Must understand role types before mapping participation across scopes',
    bidirectional: false,
  },

  // Role-Based Participation ← → DEGAP (Engage stage)
  {
    fromId: 'pf.framework.diagnostic.role.participation',
    toId: 'pf.degap.stages',
    type: 'applies-to',
    strength: 0.7,
    description: 'Role-based participation is critical during the Engage stage of DEGAP',
    bidirectional: false,
  },

  // Relevance Framework → Diagram Levels
  {
    fromId: 'gf.relevance.framework',
    toId: 'gf.diagram.levels',
    type: 'complement',
    strength: 0.6,
    description: 'Both focus on clarity and avoiding communication anti-patterns',
    bidirectional: true,
  },

  // Role Types ← → Relevance Framework
  {
    fromId: 'gf.relevance.framework',
    toId: 'pf.framework.diagnostic.role.types',
    type: 'complement',
    strength: 0.5,
    description: 'Clear role definition benefits from Relevance Framework questions',
    bidirectional: true,
  },
];

// ===== BUILD THE GRAPH =====

export function buildRelationshipGraph(): RelationshipGraph {
  const nodes = new Map<string, FrameworkMetadata>();
  
  FRAMEWORK_METADATA.forEach(framework => {
    nodes.set(framework.encodedId, framework);
  });
  
  return {
    nodes,
    edges: FRAMEWORK_RELATIONSHIPS,
  };
}

// Singleton graph instance
export const relationshipGraph = buildRelationshipGraph();

// Export aliases for easier imports
export const frameworkMetadata = FRAMEWORK_METADATA;

// Build frameworkGraph as a lookup map (framework ID -> its outgoing relationships)
export const frameworkGraph: Record<string, Array<{ targetId: string; type: FrameworkRelationship['type']; strength: number }>> = {};

FRAMEWORK_RELATIONSHIPS.forEach(rel => {
  // Add forward relationship
  if (!frameworkGraph[rel.fromId]) {
    frameworkGraph[rel.fromId] = [];
  }
  frameworkGraph[rel.fromId].push({
    targetId: rel.toId,
    type: rel.type,
    strength: rel.strength,
  });

  // Add reverse relationship if bidirectional
  if (rel.bidirectional) {
    if (!frameworkGraph[rel.toId]) {
      frameworkGraph[rel.toId] = [];
    }
    frameworkGraph[rel.toId].push({
      targetId: rel.fromId,
      type: rel.type,
      strength: rel.strength,
    });
  }
});

// Helper to get framework by ID
export function getFrameworkById(id: string): FrameworkMetadata | undefined {
  return FRAMEWORK_METADATA.find(f => f.encodedId === id);
}

// Helper to get all frameworks in a spatial location
export function getFrameworksByLocation(
  locationType: SpatialLocation['type'],
  locationId: string
): FrameworkMetadata[] {
  return FRAMEWORK_METADATA.filter(framework =>
    framework.spatialLocations.some(
      loc => loc.type === locationType && loc.id === locationId
    )
  );
}

// Helper to get all frameworks matching tags
export function getFrameworksByTags(
  scope?: FrameworkMetadata['tags']['scope'],
  context?: FrameworkMetadata['tags']['context'],
  domain?: string
): FrameworkMetadata[] {
  return FRAMEWORK_METADATA.filter(framework => {
    if (scope && framework.tags.scope !== scope) return false;
    if (context && framework.tags.context !== context) return false;
    if (domain && !framework.tags.domain?.includes(domain)) return false;
    return true;
  });
}

// Helper to get frameworks by difficulty
export function getFrameworksByDifficulty(
  difficulty: FrameworkMetadata['difficulty']
): FrameworkMetadata[] {
  return FRAMEWORK_METADATA.filter(f => f.difficulty === difficulty);
}

